import { useMemo, type ReactNode } from "react";
import { MEDIA_TYPE_LABEL, formatLabel } from "@/types/contentCalendar.type";
import type { PortalContext, PortalSlot } from "@/types/clientPortal.type";
import {
  clientClaim,
  type WindowBrief,
  type WindowPost,
} from "@/app/routes/dashboard/content-calendar/client-window/ClientMonthView";
import { useClientCalendarMonth, useClientContext } from "./api/useClientPortal";
import { useClientBriefs, type ClientBrief } from "./api/useClientBriefs";
import type { PortalSource } from "./api/clientPortalApi";
import ClientReviewPage, { type ClientReviewInitial } from "./review/ClientReviewPage";
import type { ClientPost } from "./clientReviewModel";

export function windowPost(slot: PortalSlot): WindowPost {
  const decision = slot.decision;
  const versions = slot.conceptVersions ?? [];
  const activeVersion =
    versions.find((version) => version.id === slot.activeConceptVersionId) ??
    [...versions].sort((a, b) => b.version - a.version)[0];
  const decisionValue = decision?.decision ?? activeVersion?.decision?.decision ?? null;
  const state: WindowPost["state"] =
    slot.phase === "awaiting_you"
      ? "waiting"
      : slot.phase === "in_progress"
        ? "reworking"
        : slot.phase === "missed"
          ? "missed"
          : decisionValue
            ? decisionValue === "APPROVED"
              ? "approved"
              : "declined"
            : "closed";
  return {
    id: slot.id,
    scheduledAt: slot.scheduledAt,
    pillarName: slot.pillarName,
    format: formatLabel(slot),
    title: slot.idea?.title || slot.title || "Post",
    synopsis: slot.idea?.synopsis ?? null,
    why: slot.idea ? clientClaim(slot.idea.objectiveClaim) || null : null,
    hook: slot.idea?.hook ?? null,
    reference: slot.idea?.reference ?? null,
    channels: slot.idea?.channels ?? [],
    aspectRatio: slot.idea?.aspectRatio ?? slot.aspectRatio,
    state,
    answeredBy: decision
      ? !decision.mine
        ? decision.clientName
        : null
      : activeVersion?.decision?.clientName ?? null,
    reason: decision?.reason ?? activeVersion?.decision?.reason ?? null,
    version: activeVersion?.version ?? 1,
    versionCount: versions.length || 1,
  };
}

export function windowBrief(brief: ClientBrief, readerName: string, slot?: PortalSlot): WindowBrief {
  const decision = brief.decision;
  const state: WindowBrief["state"] =
    brief.phase === "awaiting_you"
      ? "waiting"
      : brief.phase === "revised"
        ? "revised"
        : decision
          ? decision.decision === "APPROVED"
            ? "approved"
            : "declined"
          : "closed";
  return {
    id: brief.briefId,
    scheduledAt: brief.scheduledAt,
    kind: MEDIA_TYPE_LABEL[brief.kind],
    title: slot?.idea?.title || brief.ideaTitle || brief.body.title || "Untitled Creative Brief",
    synopsis: slot?.idea?.synopsis || brief.body.synopsis || null,
    state,
    answeredBy: decision && decision.clientName !== readerName ? decision.clientName : null,
    version: brief.version,
    conceptApproved:
      slot?.decision?.decision === "APPROVED" ||
      slot?.conceptVersions?.find((version) =>
        slot.activeConceptVersionId
          ? version.id === slot.activeConceptVersionId
          : version.isLatest,
      )?.decision?.decision === "APPROVED",
  };
}

export type ClientReviewBoardState =
  | { kind: "loading" }
  | { kind: "context-error" }
  /** The month or its briefs could not load; `retry` fetches them again. */
  | { kind: "month-error"; retry: () => void };

/**
 * The client review of one month (review/ClientReviewPage): the list of
 * posts, the open post with its banner, and the comments. Shared by the
 * public share-link page (source = the token) and a logged-in Guest's
 * calendar in the app (source = their brand + month).
 *
 * `renderState` draws loading and error states, which differ between a
 * standalone page and a panel inside the cockpit. `readOnly` turns every
 * answer and comment off: a guest may read a month that was never shared
 * with them for review, but not answer it.
 */
export default function ClientReviewBoard({
  source,
  embedded = false,
  toolbar,
  readOnly = false,
  theme,
  renderState,
  onSwitchReader,
  now,
  initial,
}: {
  source: PortalSource;
  embedded?: boolean;
  toolbar?: ReactNode;
  readOnly?: boolean;
  /** A standalone shell's theme (the design harness); a guest's page follows the cockpit. */
  theme?: "light" | "dark";
  renderState: (state: ClientReviewBoardState, context: PortalContext | null) => ReactNode;
  /** "Not you?" under the list: sign out. */
  onSwitchReader?: () => void;
  /** The design harness's clock and opening view. */
  now?: number;
  initial?: ClientReviewInitial;
}) {
  const contextQuery = useClientContext(source);
  const monthQuery = useClientCalendarMonth(source);
  const briefsQuery = useClientBriefs(source);

  const posts: ClientPost[] = useMemo(() => {
    const slots = monthQuery.data?.slots ?? [];
    const briefs = briefsQuery.data?.briefs ?? [];
    return slots.map((slot) => ({ slot, brief: briefs.find((brief) => brief.slotId === slot.id) ?? null }));
  }, [monthQuery.data, briefsQuery.data]);

  // The briefs are part of the page: without them an approved idea would read
  // as "Mohtawa is writing the creative brief", which may not be true.
  if (contextQuery.isLoading || (contextQuery.data && (monthQuery.isLoading || briefsQuery.isLoading))) {
    return <>{renderState({ kind: "loading" }, null)}</>;
  }
  if (contextQuery.isError || !contextQuery.data) {
    return <>{renderState({ kind: "context-error" }, null)}</>;
  }

  const { brand, month, client } = contextQuery.data;

  if (monthQuery.isError && !monthQuery.data) {
    return <>{renderState({ kind: "month-error", retry: () => void monthQuery.refetch() }, contextQuery.data)}</>;
  }
  if (briefsQuery.isError && !briefsQuery.data) {
    return <>{renderState({ kind: "month-error", retry: () => void briefsQuery.refetch() }, contextQuery.data)}</>;
  }

  return (
    <ClientReviewPage
      source={source}
      posts={posts}
      brandName={brand.name}
      year={month.year}
      month={month.month}
      readerName={client.name || client.email}
      review={monthQuery.data?.review ?? null}
      briefReview={briefsQuery.data?.review ?? null}
      readOnly={readOnly}
      embedded={embedded}
      toolbar={toolbar}
      theme={theme}
      onSwitchReader={onSwitchReader}
      now={now}
      initial={initial}
    />
  );
}
